import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vs } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";

import { MemoryIcon } from "../../components";

import { BlockContextBadge } from "./BlockContextBadge";
import { ChatThemeResultCard } from "./ChatThemeResultCard";
import {
  Message,
  hasThemeIdReference,
  stripThemeIdReferences,
  isUserMessage,
  hasDashboardModification,
  getActionLabel,
  getMessageText,
} from "./messageUtils";
import { splitThemeRefs } from "./themeChipRef";
import { ThemeSwatchBadge } from "./ThemeSwatchBadge";

const UndoIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.53532 4.31038C2.19256 4.63564 2 5.07688 2 5.53699C2 5.99711 2.19256 6.4384 2.53532 6.76366L4.72438 8.84179C4.93358 9.04039 5.23847 9.11794 5.52426 9.04525C5.81003 8.97255 6.03325 8.76065 6.10982 8.48936C6.1864 8.21807 6.1047 7.92861 5.8955 7.73002L4.412 6.3217H9.655C10.616 6.3217 11.5039 6.80839 11.9844 7.59843C12.4648 8.38847 12.4648 9.3618 11.9844 10.1519C11.5039 10.9419 10.616 11.4286 9.655 11.4286H3.65473C3.35906 11.4286 3.08581 11.5784 2.93797 11.8215C2.79014 12.0646 2.79014 12.3641 2.93797 12.6072C3.08581 12.8503 3.35906 13 3.65473 13H9.655C10.8074 13 11.9125 12.5654 12.7274 11.7919C13.5422 11.0183 14 9.96908 14 8.87518C14 7.78128 13.5422 6.73207 12.7274 5.9585C11.9125 5.18496 10.8073 4.75036 9.655 4.75036H4.412L5.8955 3.34204C6.1047 3.14344 6.18639 2.854 6.10982 2.5827C6.03324 2.31141 5.81003 2.0995 5.52426 2.02681C5.23849 1.95411 4.93358 2.03167 4.72438 2.23027L2.53532 4.31038Z"
      fill="currentColor"
    />
  </svg>
);

export type { Message };

// Unescape block reference values (reverse of escapeForBlockRef in WebDashboardChatSidebar)
const unescapeBlockRef = (str: string): string => str.replace(/\\"/g, '"').replace(/\\\\/g, "\\");

// Parse message text and render block references as badges
const renderMessageWithBlockBadges = (
  text: string,
  blockTypeById?: Record<string, string>
): React.ReactNode => {
  // Format: [block id="blockId" title="blockTitle"]
  // Matches content with escaped quotes (\" or \\) or non-quote/non-backslash characters
  const blockRefPattern = /\[block id="((?:[^"\\]|\\.)*)\" title="((?:[^"\\]|\\.)*)"\]/g;

  const parts: React.ReactNode[] = [];

  let lastIndex = 0;

  let match: RegExpExecArray | null;

  while ((match = blockRefPattern.exec(text)) !== null) {
    // Add text before the match. Trimmed because the container is a flex row: spacing comes from
    // the flex gap, and stray leading/trailing whitespace would either collapse to zero or push
    // the badge out of alignment with the text.
    if (match.index > lastIndex) {
      const segment = text.slice(lastIndex, match.index).trim();
      if (segment) {
        parts.push(segment);
      }
    }

    const blockId = unescapeBlockRef(match[1]);
    const blockTitle = unescapeBlockRef(match[2]);

    parts.push(
      <BlockContextBadge
        key={match.index}
        blockTitle={blockTitle}
        blockType={blockTypeById?.[blockId]}
      />
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text (trimmed — see note above).
  if (lastIndex < text.length) {
    const tail = text.slice(lastIndex).trim();
    if (tail) {
      parts.push(tail);
    }
  }

  return parts.length > 0 ? parts : text;
};

/**
 * Render a user message's theme references (`[theme id="…" colors="…"]`) as swatch chips, the same
 * way the composer showed them when the message was sent.
 */
const renderThemeRefParts = (rendered: React.ReactNode): React.ReactNode => {
  const parts = Array.isArray(rendered) ? rendered : [rendered];
  return parts.flatMap((part, partIndex) => {
    if (typeof part !== "string") {
      return [part];
    }
    // Trimmed like the block-badge segments: the container is a flex row, so spacing comes from
    // the gap and stray whitespace would push a chip out of alignment.
    return splitThemeRefs(part)
      .map((segment, i) =>
        segment.themeRef ? (
          <ThemeSwatchBadge key={`theme-${partIndex}-${i}`} colors={segment.themeRef.colors} />
        ) : (
          segment.text?.trim()
        )
      )
      .filter(Boolean);
  });
};

type AutoFixMessageMeta = {
  is_auto_fix?: boolean;
  attempt_index?: number;
  max_attempts?: number;
  source?: "fix_button" | "auto_fix_retry";
};

function getAutoFixMeta(message: Message): AutoFixMessageMeta | undefined {
  return (message.content as any)?.auto_fix as AutoFixMessageMeta | undefined;
}

function isAutoFixUserMessage(message: Message): boolean {
  return Boolean(getAutoFixMeta(message)?.is_auto_fix);
}

function isFixButtonMessage(meta?: AutoFixMessageMeta): boolean {
  return meta?.source === "fix_button";
}

/**
 * Extract block references from the message text so we can show "Fixing @block_name"
 * for fix-button messages instead of the raw error text.
 */
function renderFixButtonMessageCosmetic(
  messageText: string,
  blockTypeById?: Record<string, string>
): React.ReactNode {
  const blockRefPattern = /\[block id="((?:[^"\\]|\\.)*)\" title="((?:[^"\\]|\\.)*)"\]/g;
  const match = blockRefPattern.exec(messageText);
  if (match) {
    const blockId = unescapeBlockRef(match[1]);
    const blockTitle = unescapeBlockRef(match[2]);
    return (
      <>
        {"Fix "}
        <BlockContextBadge blockTitle={blockTitle} blockType={blockTypeById?.[blockId]} />
      </>
    );
  }

  const slicerRefPattern = /\[slicer name="((?:[^"\\]|\\.)*)"\]/;
  const slicerMatch = slicerRefPattern.exec(messageText);
  if (slicerMatch) {
    const slicerName = unescapeBlockRef(slicerMatch[1]);
    return (
      <>
        {"Fix filter "}
        <BlockContextBadge blockTitle={slicerName} />
      </>
    );
  }

  return "Fix this block…";
}

function renderAutoFixRetryMessageCosmetic(meta?: AutoFixMessageMeta): string {
  const attempt = meta?.attempt_index;
  const maxAttempts = meta?.max_attempts;
  return attempt
    ? `[Auto-fix] Some dashboard blocks didn't render correctly. Fixing it now… (Attempt ${attempt}/${
        maxAttempts ?? 3
      })`
    : `[Auto-fix] Some dashboard blocks didn't render correctly. Fixing it now…`;
}

// Shared markdown component config, used both for the standalone assistant bubble and the
// dashboard action card's expandable details so both render formatting identically.
const markdownComponents = {
  code: ({
    node,
    inline,
    className,
    children,
    ...props
  }: {
    node?: React.ReactNode;
    inline?: boolean;
    className?: string;
    children?: React.ReactNode;
  }) => {
    const match = /language-(\w+)/.exec(className || "");
    return !inline && match ? (
      <SyntaxHighlighter
        style={vs}
        language={match[1]}
        PreTag="div"
        customStyle={{
          margin: 0,
          padding: "12px",
          borderRadius: "6px",
          backgroundColor: "#f3f4f6",
          fontSize: "13px",
        }}
        {...props}
      >
        {String(children).replace(/\n$/, "")}
      </SyntaxHighlighter>
    ) : (
      <code
        className={`${className || ""} bg-gray-200 px-1.5 py-0.5 rounded text-[13px]`}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }: { children?: React.ReactNode }) => (
    <div className="my-2 overflow-x-auto">{children}</div>
  ),
  p: ({ children }: { children?: React.ReactNode }) => <p className="my-2">{children}</p>,
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="my-2 pl-5 list-disc">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="my-2 pl-5 list-decimal">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => <li className="my-1">{children}</li>,
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
      {children}
    </a>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-2xl font-semibold mt-3 mb-2">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-xl font-semibold mt-3 mb-2">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-lg font-semibold mt-2.5 mb-1.5">{children}</h3>
  ),
};

const MarkdownText: React.FC<{ text: string; className?: string }> = ({
  text,
  className = "text-sm overflow-hidden",
}) => (
  <div className={className}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {text}
    </ReactMarkdown>
  </div>
);

interface Props {
  message: Message;
  /** Callback to revert this message's changes */
  onRevert?: (messageId: string) => void;
  /** Whether this message's undo entry still exists in the stack (can be reverted) */
  canRevert?: boolean;
  /** Force whether to show the dashboard action card for this message */
  showDashboardActionCard?: boolean;
  /** Optional action label override for dashboard action card */
  actionLabelOverride?: string;
  /** Optional target message ID to use for revert action */
  revertTargetMessageId?: string;
  /** Whether the dashboard action card details start expanded (only the latest card should). */
  defaultDetailsExpanded?: boolean;
  /** Maps block_id -> block type so message block badges can show the per-type icon. */
  blockTypeById?: Record<string, string>;
  /** Confirm/reject a pending save_memory proposal (echoes proposal_id to the server). */
  onSaveMemoryDecision?: (proposalId: string, decision: "confirm" | "reject") => Promise<void>;
  /** Opens the AI memory drawer (from the confirm card's "View memory" link). */
  onOpenMemoryDrawer?: () => void;
  /** Whether this message's save-memory confirm buttons are still actionable. */
  saveMemoryActionable?: boolean;
  /** Live-preview a custom theme from this message's theme result card. */
  onThemeCardPreview?: (themePointer: string) => void;
  /** Persist a custom theme from the theme result card. */
  onThemeCardApply?: (themePointer: string) => void;
  /** Revert a theme result card's live preview. */
  onThemeCardCancelPreview?: () => void;
  /** Whether this message's theme card should auto-preview (fresh replies only). */
  shouldAutoPreviewTheme?: boolean;
}

// Memoized to prevent re-renders when the parent's state changes (e.g., typing in ChatInput).
// Each message uses expensive ReactMarkdown + SyntaxHighlighter rendering, so avoiding
// unnecessary re-renders significantly improves input responsiveness.
export const MessageComponent: React.FC<Props> = React.memo(
  ({
    message,
    onRevert,
    canRevert = false,
    showDashboardActionCard,
    actionLabelOverride,
    revertTargetMessageId,
    defaultDetailsExpanded = false,
    blockTypeById,
    onSaveMemoryDecision,
    onOpenMemoryDrawer,
    saveMemoryActionable = false,
    onThemeCardPreview,
    onThemeCardApply,
    onThemeCardCancelPreview,
    shouldAutoPreviewTheme = false,
  }) => {
    const isUser = isUserMessage(message);
    const hasDashboardMod = hasDashboardModification(message);
    const [isReverting, setIsReverting] = useState(false);
    const [isDetailsExpanded, setIsDetailsExpanded] = useState(defaultDetailsExpanded);
    const [pendingDecision, setPendingDecision] = useState<"confirm" | "reject" | null>(null);
    // Optimistic post-answer state for the save-to-memory block (server persists the
    // durable copy onto the message's save_memory_confirmation.decision).
    const [localMemoryDecision, setLocalMemoryDecision] = useState<"saved" | "declined" | null>(
      null
    );

    const actionLabel = actionLabelOverride ?? getActionLabel(message);
    const shouldShowDashboardActionCard = !isUser && (showDashboardActionCard ?? hasDashboardMod);

    // Track mounted state to prevent state updates after unmount (e.g., when message is hidden after revert)
    const isMountedRef = useRef(true);
    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    const handleRevertClick = async () => {
      if (onRevert && !isReverting) {
        setIsReverting(true);
        try {
          await onRevert(revertTargetMessageId ?? message.id);
        } finally {
          if (isMountedRef.current) {
            setIsReverting(false);
          }
        }
      }
    };

    const messageText = getMessageText(message);
    const hasCardDetails = Boolean(messageText);

    // User messages: render directly with block badges (no markdown parsing)
    // Assistant messages: render with ReactMarkdown for formatting
    const renderMessageContent = () => {
      if (isUser) {
        const autoFixMeta = getAutoFixMeta(message);
        if (isAutoFixUserMessage(message)) {
          if (isFixButtonMessage(autoFixMeta)) {
            return (
              <div className="text-sm">
                {renderFixButtonMessageCosmetic(messageText, blockTypeById)}
              </div>
            );
          }
          return <div className="text-sm">{renderAutoFixRetryMessageCosmetic(autoFixMeta)}</div>;
        }
        return (
          <div className="text-sm flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0 wrap-anywhere">
            {renderThemeRefParts(
              renderMessageWithBlockBadges(
                hasThemeIdReference(message) ? stripThemeIdReferences(messageText) : messageText,
                blockTypeById
              )
            )}
          </div>
        );
      }

      return <MarkdownText text={messageText} />;
    };

    // Save-to-memory confirmation block (product spec, Ashley 7/17): fixed "Save to
    // memory?" title, the agent-authored memory line as the body, "Applies to all your
    // dashboards" caption, Yes/No while actionable. The OUTCOME lives in this block
    // (server persists `decision` onto the card message; there is no separate chat
    // acknowledgment): Saved → "✓ Saved · View memory"; Declined → "Not saved" (dimmed);
    // no response → "Not saved · You can ask me to remember this anytime". Built as a
    // variable (not an exclusive early return) because a turn that edited the dashboard
    // and then paused on save_memory can land BOTH web_dashboard_json and
    // save_memory_confirmation on one persisted message — the dashboard action card
    // (with its undo affordance) must still render; this block stacks below it.
    const saveMemoryConfirmation = message.content.save_memory_confirmation ?? undefined;
    const handleSaveMemoryDecisionClick = async (decision: "confirm" | "reject") => {
      if (!onSaveMemoryDecision || !saveMemoryConfirmation || pendingDecision) {
        return;
      }
      setPendingDecision(decision);
      try {
        await onSaveMemoryDecision(saveMemoryConfirmation.proposal_id, decision);
        // Optimistic: flip the block immediately; the poll refetch brings the
        // server-persisted decision on the message itself.
        if (isMountedRef.current) {
          setLocalMemoryDecision(decision === "confirm" ? "saved" : "declined");
        }
      } finally {
        if (isMountedRef.current) {
          setPendingDecision(null);
        }
      }
    };
    // create_custom_theme result: swatch card with live preview + Apply. Stacks below the
    // message text (and below the dashboard action card when both land on one message).
    const createdCustomTheme = message.content.created_custom_theme ?? undefined;
    const themeResultCard =
      !isUser && createdCustomTheme && onThemeCardPreview && onThemeCardApply ? (
        <ChatThemeResultCard
          summary={createdCustomTheme}
          shouldAutoPreview={shouldAutoPreviewTheme}
          onPreviewTheme={onThemeCardPreview}
          onApplyTheme={onThemeCardApply}
          onCancelPreview={onThemeCardCancelPreview ?? (() => undefined)}
        />
      ) : null;

    // Server-persisted outcome wins; local optimistic state covers the refetch gap.
    const memoryDecision = saveMemoryConfirmation?.decision ?? localMemoryDecision;
    const showMemoryButtons =
      !memoryDecision && saveMemoryActionable && Boolean(onSaveMemoryDecision);
    // Figma (AI-Dashboards → Memories): pending proposal is a blue-tinted card with white
    // Yes/No buttons in brand blue; decided cards are white with a muted footer line and a
    // right-aligned underlined "View memory" link.
    const saveMemoryCard =
      !isUser && saveMemoryConfirmation ? (
        <div
          className={`rounded-lg ${
            showMemoryButtons ? "bg-[#eff4fe]" : "bg-white border border-gray-100"
          }`}
        >
          {/* Design review: a touch more air between title, body, and button row; Yes/No
              at 14px (the iframe pins text-sm to 13px, which read as narrower buttons). */}
          <div className="px-4 pt-3 pb-3.5 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <MemoryIcon width="18px" height="18px" className="shrink-0 text-gray-900" />
              <span className="text-sm font-bold text-[#111215]">Save to memory?</span>
            </div>
            <div className="text-sm text-black leading-5">
              {saveMemoryConfirmation.summary || "Save this preference to memory."}
            </div>
            {showMemoryButtons ? (
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => handleSaveMemoryDecisionClick("confirm")}
                  disabled={pendingDecision !== null}
                  className="h-8 px-6 text-[14px] font-semibold text-[#3053ff] bg-white rounded-[4px] hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {pendingDecision === "confirm" ? "Saving…" : "Yes"}
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveMemoryDecisionClick("reject")}
                  disabled={pendingDecision !== null}
                  className="h-8 px-6 text-[14px] font-semibold text-[#3053ff] bg-white rounded-[4px] hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {pendingDecision === "reject" ? "…" : "No"}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 pt-1">
                <span className="flex-1 text-xs font-medium text-[#727272]">
                  {memoryDecision === "saved"
                    ? "✓ Saved"
                    : memoryDecision === "declined"
                    ? "Not saved"
                    : "Not saved · You can ask me to remember this anytime"}
                </span>
                {onOpenMemoryDrawer && (
                  <button
                    type="button"
                    onClick={onOpenMemoryDrawer}
                    className="text-xs font-medium text-[#111215] underline hover:text-gray-600 cursor-pointer whitespace-nowrap"
                  >
                    View memory
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null;

    if (saveMemoryCard && !shouldShowDashboardActionCard) {
      return <div data-message-id={message.id}>{saveMemoryCard}</div>;
    }

    // Agent messages with dashboard modifications - show header with undo button
    if (shouldShowDashboardActionCard) {
      return (
        <div data-message-id={message.id} className="flex flex-col gap-2">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="material-icons text-purple-500" style={{ fontSize: 18 }}>
                  auto_awesome
                </span>
                <span className="text-sm font-medium text-gray-900">{actionLabel}</span>
              </div>
              <div className="flex items-center gap-1">
                {hasCardDetails && (
                  <button
                    type="button"
                    onClick={() => setIsDetailsExpanded(prev => !prev)}
                    title={isDetailsExpanded ? "Collapse details" : "Expand details"}
                    className="inline-flex h-7 w-7 items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                  >
                    <span className="material-icons leading-none" style={{ fontSize: 18 }}>
                      {isDetailsExpanded ? "expand_less" : "expand_more"}
                    </span>
                  </button>
                )}
                {canRevert && !isReverting && (
                  <button
                    type="button"
                    onClick={handleRevertClick}
                    title="Undo changes"
                    className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                  >
                    {UndoIcon}
                  </button>
                )}
                {isReverting && <span className="text-sm text-gray-500">...</span>}
              </div>
            </div>
            {hasCardDetails && isDetailsExpanded && (
              <div className="px-3 pb-2">
                <MarkdownText
                  text={messageText}
                  className="text-xs text-gray-700 break-words wrap-anywhere overflow-hidden"
                />
              </div>
            )}
          </div>
          {saveMemoryCard}
          {themeResultCard}
        </div>
      );
    }

    // User messages and agent messages without modifications
    return (
      <div
        data-message-id={message.id}
        className={`${
          isUser
            ? "bg-gray-100 text-gray-800 text-left p-2 rounded-lg ml-auto max-w-[85%] break-words wrap-anywhere"
            : "bg-white text-gray-800 text-left p-2 rounded-lg break-words wrap-anywhere"
        }`}
      >
        {renderMessageContent()}
        {themeResultCard}
      </div>
    );
  }
);

MessageComponent.displayName = "MessageComponent";
